'use client';
import React, { useEffect, useState } from 'react';
import SinglePostCard from '@/components/blog/SinglePostCard';
import searchClient from '@/lib/algolia/searchClient';
import { index } from '@/lib/algolia/client.env';

import BlogBottomPagination from '@/components/blog/BlogList/BlogBottomPagination';
import PaginatedPostsLoader from '@/components/blog/BlogList/PaginatedPostsLoader';

interface ResultProps {
  page: number;
  query?: string;
  searchFilters?: {
    category: string | null;
    tag: string | null;
  };
}

export const BlogSearchResultsListView: React.FC<ResultProps> = ({
  page,
  query = '',
  searchFilters,
}) => {
  const maxHitsPerPage = 20;

  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const buildFilters = () => {
      const filters = [];

      if (searchFilters?.category) {
        filters.push([`category.slug:${searchFilters.category}`]);
      }

      if (searchFilters?.tag) {
        // Ensure exact match for a tag slug in the tags array
        filters.push([`tags.slug:${searchFilters.tag}`]);
      }

      return filters;
    };

    const filters = buildFilters();

    const fetchResults = async () => {
      setLoading(true);
      const { results }: any = await searchClient.search({
        requests: [
          {
            indexName: index,
            query: query,
            hitsPerPage: maxHitsPerPage,
            page: page - 1,
            attributesToHighlight: ['title', 'seoDescription'],

            facetFilters: filters,
            facets: ['*'],
          },
        ],
      });
      setResults(results);

      // scroll to top
      window.scrollTo(0, 0);
      setLoading(false);
    };

    fetchResults();
  }, [query, page, searchFilters]);

  if (loading) {
    return <PaginatedPostsLoader />;
  }

  const [resultObject] = results;
  let {
    hits: articles,
    nbHits,
    nbPages,
    page: currentPage,
    hitsPerPage,
  } = resultObject;

  const pageFrom = currentPage * hitsPerPage;
  const pageTo =
    currentPage * hitsPerPage + hitsPerPage > nbHits
      ? nbHits
      : currentPage * hitsPerPage + hitsPerPage;

  if (articles.length === 0) {
    return (
      <div className='py-10 text-center'>
        <h2 className='mb-4 text-2xl font-semibold'>No results found</h2>
        <p className='text-gray-600'>
          Oops! It seems we couldn&apos;t find any articles matching your
          search. Try adjusting your search terms or explore other categories.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className='grid gap-8 sm:grid-cols-2'>
        {articles &&
          articles.map((article: any) => (
            <SinglePostCard
              key={article._id}
              post={{
                ...article,
                image: {
                  url: article.image,
                },
                excerpt: article.seoDescription,
              }}
              headingClass='link-underline'
              className='w-1/2 [&_.content]:w-[75%]'
            />
          ))}
      </div>

      <BlogBottomPagination
        pageFrom={pageFrom + 1}
        pageTo={pageTo}
        totalHits={nbHits}
        currentPage={currentPage + 1}
        searchQuery={query}
        searchFilters={searchFilters}
        numberOfPages={nbPages}
        tag='results'
      />
    </>
  );
};
